import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { api, tokenStore } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `loading` starts true so ProtectedRoute doesn't bounce a signed-in user to
  // /login during the initial token check.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    api
      .get('/auth/me')
      .then((data) => {
        if (!cancelled) setUser(data.user);
      })
      .catch(() => {
        // Expired or tampered token — clear it rather than leaving a broken session.
        tokenStore.clear();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const applySession = useCallback((data) => {
    tokenStore.set(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(
    async (credentials) => applySession(await api.post('/auth/login', credentials, { auth: false })),
    [applySession]
  );

  const signup = useCallback(
    async (details) => applySession(await api.post('/auth/signup', details, { auth: false })),
    [applySession]
  );

  /** Unlocks the admin role on an existing account using the church's key. */
  const elevate = useCallback(
    async (adminKey) => applySession(await api.post('/auth/elevate', { adminKey })),
    [applySession]
  );

  const logout = useCallback(() => {
    tokenStore.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === 'admin',
      login,
      signup,
      logout,
      elevate,
    }),
    [user, loading, login, signup, logout, elevate]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>.');
  return ctx;
}
